import type { Component } from "solid-js"
import {
  Button,
  Card,
  Column,
  Divider,
  EmptyState,
  ErrorBanner,
  FilterBar,
  FilterBarField,
  Form,
  FormField,
  Heading,
  KpiStat,
  ResultCard,
  Row,
  Section,
  Select,
  StatusChip,
  TabGroup,
  TextInput,
  Textarea,
} from "@openanalyst-ai/ui/registry"
import { VisualPipelineRegistry } from "./visual-pipeline-registry"

export type RegistryComponent = Component<Record<string, unknown>>

export type RegistryCatalogEntry = {
  component: RegistryComponent
  authorable: boolean
  props: readonly string[]
  slots: readonly string[]
  runtime?: "submit"
}

export const REGISTRY_CATALOG: Record<string, RegistryCatalogEntry> = {
  "layout.section.v1": {
    component: Section as unknown as RegistryComponent,
    authorable: true,
    props: ["density"],
    slots: ["title", "description", "toolbar", "children"],
  },
  "layout.card.v1": {
    component: Card as unknown as RegistryComponent,
    authorable: true,
    props: ["elevation"],
    slots: ["children"],
  },
  "layout.row.v1": {
    component: Row as unknown as RegistryComponent,
    authorable: true,
    props: ["gap", "align", "justify", "wrap"],
    slots: ["children"],
  },
  "layout.column.v1": {
    component: Column as unknown as RegistryComponent,
    authorable: true,
    props: ["gap", "align", "justify", "wrap"],
    slots: ["children"],
  },
  "layout.divider.v1": {
    component: Divider as unknown as RegistryComponent,
    authorable: true,
    props: ["orientation", "weight"],
    slots: [],
  },
  "layout.tab-group.v1": {
    component: TabGroup as unknown as RegistryComponent,
    authorable: false,
    props: ["tabs", "value"],
    slots: [],
  },
  "atomic.button.v1": {
    component: Button as unknown as RegistryComponent,
    authorable: false,
    props: ["intent", "variant", "size", "disabled"],
    slots: ["children"],
  },
  "atomic.text-input.v1": {
    component: TextInput as unknown as RegistryComponent,
    authorable: false,
    props: ["value", "placeholder", "disabled"],
    slots: [],
  },
  "atomic.textarea.v1": {
    component: Textarea as unknown as RegistryComponent,
    authorable: false,
    props: ["value", "placeholder", "disabled"],
    slots: [],
  },
  "atomic.select.v1": {
    component: Select as unknown as RegistryComponent,
    authorable: false,
    props: ["value", "options", "disabled"],
    slots: [],
  },
  "display.heading.v1": {
    component: Heading as unknown as RegistryComponent,
    authorable: true,
    props: ["intent", "id"],
    slots: ["children"],
  },
  "display.kpi-stat.v1": {
    component: KpiStat as unknown as RegistryComponent,
    authorable: true,
    props: ["label", "value", "delta", "trend", "hint"],
    slots: [],
  },
  "display.status-chip.v1": {
    component: StatusChip as unknown as RegistryComponent,
    authorable: true,
    props: ["label", "intent", "icon"],
    slots: [],
  },
  "display.empty-state.v1": {
    component: EmptyState as unknown as RegistryComponent,
    authorable: true,
    props: ["title", "description", "icon"],
    slots: ["action"],
  },
  "composite.error-banner.v1": {
    component: ErrorBanner as unknown as RegistryComponent,
    authorable: true,
    props: ["intent", "title", "description", "icon"],
    slots: ["description", "action"],
  },
  "composite.filter-bar.v1": {
    component: FilterBar as unknown as RegistryComponent,
    authorable: false,
    props: [],
    slots: ["children"],
  },
  "composite.filter-bar.field.v1": {
    component: FilterBarField as unknown as RegistryComponent,
    authorable: false,
    props: ["label"],
    slots: ["children"],
  },
  "composite.form.v1": {
    component: Form as unknown as RegistryComponent,
    authorable: false,
    props: [],
    slots: ["children"],
  },
  "composite.form.field.v1": {
    component: FormField as unknown as RegistryComponent,
    authorable: false,
    props: ["label"],
    slots: ["children"],
  },
  "composite.result-card.v1": {
    component: ResultCard as unknown as RegistryComponent,
    authorable: true,
    props: [],
    slots: ["media", "title", "subtitle", "meta", "description", "actions"],
  },
  "workflow.visual-pipeline.v1": {
    component: VisualPipelineRegistry as unknown as RegistryComponent,
    authorable: true,
    props: ["title", "description", "mode", "graph", "nodeTypes", "templates", "validation", "height"],
    slots: [],
    runtime: "submit",
  },
}

export function registryKey(componentId: string, version: string) {
  return `${componentId}.${version}`
}

export function registryEntry(componentId: string, version: string) {
  return REGISTRY_CATALOG[registryKey(componentId, version)]
}

export function registryComponentExists(componentId: string, version: string) {
  return registryKey(componentId, version) in REGISTRY_CATALOG
}

export function registryComponentAuthorable(componentId: string, version: string) {
  return Boolean(registryEntry(componentId, version)?.authorable)
}

export function registryAuthorableKeys() {
  return Object.entries(REGISTRY_CATALOG)
    .filter((entry) => entry[1].authorable)
    .map((entry) => entry[0])
}
